import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, RefreshCw } from "lucide-react";
import SEO from "@/components/SEO";
import { api, resolveImage } from "@/lib/api";

// 4-question scent quiz. Each answer bumps weights on our known scent traits;
// the highest-weight product wins. Traits align with product slug names so
// non-devs can add new products without touching this file.
const QUESTIONS = [
  {
    q: "Your Friday-night energy is…",
    options: [
      { label: "Sweet & flirty", weights: { "blush-and-bloom": 3, "cherry-bomb": 2, "freshly-baked": 1 } },
      { label: "Bold & mischievous", weights: { "cherry-bomb": 3, "sugared-skin": 2, "deep-blue": 1 } },
      { label: "Grown & smooth", weights: { "bare-wood": 3, "deep-blue": 2, "sugared-skin": 1 } },
      { label: "Soft & cozy", weights: { "freshly-baked": 3, "sugared-skin": 2, "blush-and-bloom": 1 } },
    ],
  },
  {
    q: "Pick a first-date smell.",
    options: [
      { label: "Fresh cut flowers", weights: { "blush-and-bloom": 3 } },
      { label: "Warm vanilla skin", weights: { "sugared-skin": 3, "freshly-baked": 1 } },
      { label: "Sandalwood cologne", weights: { "bare-wood": 3, "deep-blue": 1 } },
      { label: "Bakery on Sunday morning", weights: { "freshly-baked": 3 } },
      { label: "Ocean at midnight", weights: { "deep-blue": 3 } },
      { label: "Ripe fruit + trouble", weights: { "cherry-bomb": 3 } },
    ],
  },
  {
    q: "What's your car saying about you right now?",
    options: [
      { label: "Just detailed. Immaculate.", weights: { "bare-wood": 2, "deep-blue": 2 } },
      { label: "Coffee cup in every cupholder.", weights: { "freshly-baked": 2, "sugared-skin": 2 } },
      { label: "Ready to be seen at Trader Joe's.", weights: { "blush-and-bloom": 2, "cherry-bomb": 2 } },
      { label: "Late-night drive vibes only.", weights: { "deep-blue": 3, "cherry-bomb": 1 } },
    ],
  },
  {
    q: "Which vibe wins?",
    options: [
      { label: "Rose petals on the dashboard", weights: { "blush-and-bloom": 3 } },
      { label: "Cinnamon-sugar donut", weights: { "freshly-baked": 3 } },
      { label: "Vanilla musk on bare skin", weights: { "sugared-skin": 3 } },
      { label: "A dark-wood barroom", weights: { "bare-wood": 3 } },
      { label: "A fireworks-red cherry", weights: { "cherry-bomb": 3 } },
      { label: "A moonlit beach", weights: { "deep-blue": 3 } },
    ],
  },
];

export default function ScentQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => setProducts([]));
  }, []);

  const done = step >= QUESTIONS.length;
  const totals = useMemo(() => {
    const t = {};
    answers.forEach((a) => Object.entries(a?.weights || {}).forEach(([k, v]) => { t[k] = (t[k] || 0) + v; }));
    return t;
  }, [answers]);

  const winnerSlug = useMemo(() => {
    const entries = Object.entries(totals);
    if (!entries.length) return null;
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0];
  }, [totals]);
  const winner = products.find((p) => p.id === winnerSlug);

  const restart = () => { setStep(0); setAnswers([]); };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Quiz",
    "name": "Booty Breeze Scent Quiz",
    "about": "Which car freshener scent matches your personality",
    "url": "https://bootybreeze.com/scent-quiz",
    "educationalLevel": "Beginner",
    "numberOfQuestions": QUESTIONS.length,
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16" data-testid="scent-quiz-page">
      <SEO
        title="Which Car Freshener Scent Matches Your Vibe? — Booty Breeze Quiz"
        description="Take the 60-second scent quiz — 4 questions that pair you with the Booty Breeze freshener your car has been asking for. Free to try."
        canonical="/scent-quiz"
        jsonLd={jsonLd}
      />
      <div className="mb-6">
        <Link to="/" data-testid="quiz-home-link" className="text-white/50 hover:text-white text-xs uppercase tracking-widest">← Home</Link>
      </div>
      <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3 flex items-center gap-2">
        <Sparkles size={14} /> Scent quiz · {QUESTIONS.length} questions
      </div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mb-4">Find your scent in 60 seconds.</h1>
      <p className="text-white/60 text-base mb-10 max-w-xl">Small-batch. Hand-poured. Six scents. Answer four questions and we'll match you.</p>

      {!done && (
        <div className="rounded-2xl border border-white/10 bg-[#0f0f0f] p-6 sm:p-10" data-testid={`quiz-step-${step}`}>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-3">Question {step + 1} of {QUESTIONS.length}</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-6">{QUESTIONS[step].q}</h2>
          <div className="grid gap-3">
            {QUESTIONS[step].options.map((opt, i) => (
              <button
                key={i}
                data-testid={`quiz-option-${step}-${i}`}
                onClick={() => { setAnswers([...answers, opt]); setStep(step + 1); }}
                className="text-left px-5 py-4 rounded-xl border border-white/10 bg-white/[.02] hover:border-[#FF3D8B] hover:bg-[#FF3D8B]/5 text-white transition-all group flex items-center justify-between"
              >
                <span>{opt.label}</span>
                <ArrowRight size={16} className="text-white/30 group-hover:text-[#FF3D8B]" />
              </button>
            ))}
          </div>
          {step > 0 && (
            <button onClick={() => { setStep(step - 1); setAnswers(answers.slice(0, -1)); }}
              data-testid="quiz-back-btn"
              className="mt-6 text-white/40 hover:text-white text-xs uppercase tracking-widest">← Back</button>
          )}
        </div>
      )}

      {done && winner && (
        <div className="rounded-2xl border border-white/10 bg-[#0f0f0f] p-8 sm:p-12 text-center" data-testid="quiz-result">
          <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3">Your match</div>
          <div className="mx-auto w-40 h-40 mb-6 relative">
            <img src={resolveImage(winner.image_url)} alt={winner.name}
              className="w-full h-full object-contain drop-shadow-[0_20px_40px_rgba(255,61,139,0.35)]" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-white mb-3">{winner.name}</h2>
          <p className="text-white/70 text-base mb-8 max-w-md mx-auto">{winner.tagline || winner.description}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to={`/product/${winner.id}`} data-testid="quiz-result-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0]">
              Grab this scent <ArrowRight size={14} />
            </Link>
            <button onClick={restart} data-testid="quiz-restart-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-[#A855F7] text-xs uppercase tracking-widest">
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        </div>
      )}

      {done && !winner && (
        <div className="rounded-2xl border border-white/10 bg-[#0f0f0f] p-8 text-white/60" data-testid="quiz-result-empty">
          Couldn't match a product — <button className="text-[#FF3D8B]" onClick={restart}>try again</button>.
        </div>
      )}
    </div>
  );
}
